'use client'

import Image from 'next/image'
import { useMemo, useRef, useState, type SyntheticEvent } from 'react'
import { ReactCompareSlider } from 'react-compare-slider'
import { getOptimizedImageProps } from '@inoo-ch/payload-image-optimizer/frontend'

import ThemeIcon from '@/components/ui/ThemeIcon'
import type { Media as MediaType } from '@/payload-types'

export interface LightboxBeforeAfterImage {
  src: string
  alt: string
  width?: number
  height?: number
  blurDataURL?: string
  resource?: MediaType | null
}

export interface LightboxBeforeAfterSlideProps {
  before: LightboxBeforeAfterImage
  after: LightboxBeforeAfterImage
  rect?: { width: number; height: number }
  padding?: number
  defaultPosition?: number
  leftLabel?: string
  rightLabel?: string
}

const TRANSITION = 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)'

function computeFitSize(
  intrinsicWidth: number | undefined,
  intrinsicHeight: number | undefined,
  rect: { width: number; height: number } | undefined,
  padding: number,
): { width: number; height: number } {
  const viewportWidth = rect?.width ?? 0
  const viewportHeight = rect?.height ?? 0
  const maxWidth = Math.max(viewportWidth - padding, 1)
  const maxHeight = Math.max(viewportHeight - padding, 1)
  const baseWidth = intrinsicWidth && intrinsicWidth > 0 ? intrinsicWidth : maxWidth
  const baseHeight = intrinsicHeight && intrinsicHeight > 0 ? intrinsicHeight : maxHeight
  const scale = Math.min(1, maxWidth / baseWidth, maxHeight / baseHeight)
  const appliedScale = Number.isFinite(scale) && scale > 0 ? scale : 1
  return {
    width: Math.round(baseWidth * appliedScale),
    height: Math.round(baseHeight * appliedScale),
  }
}

function SlideImage({
  image,
  width,
  height,
}: {
  image: LightboxBeforeAfterImage
  width: number
  height: number
}) {
  const [loaded, setLoaded] = useState(false)
  const lastSrc = useRef<string | null>(null)

  if (lastSrc.current !== image.src) {
    lastSrc.current = image.src
    if (loaded) setLoaded(false)
  }

  // Plugin returns a variant-aware loader that serves pre-generated
  // size variants directly when available. Fallback path emits q=80
  // which next.config.js rejects (qualities: [25,50,75,90,95]) — we
  // must pair the loader with quality={75} on the <Image>.
  const optimized = useMemo(() => {
    if (image.resource && typeof image.resource === 'object') {
      return getOptimizedImageProps(image.resource as any)
    }
    return null
  }, [image.resource])

  const handleLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget
    if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      setLoaded(true)
    }
  }

  return (
    <div className="relative h-full w-full">
      {image.blurDataURL && (
        <div
          aria-hidden="true"
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            backgroundImage: `url(${image.blurDataURL})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            transition: TRANSITION,
            opacity: loaded ? 0 : 1,
          }}
        />
      )}
      <Image
        src={image.src}
        alt={image.alt}
        width={width}
        height={height}
        draggable={false}
        sizes="100vw"
        quality={75}
        {...(optimized?.loader ? { loader: optimized.loader } : {})}
        onLoad={handleLoad}
        className="relative z-10 h-full w-full object-cover"
        style={{
          transition: TRANSITION,
          opacity: loaded ? 1 : 0,
        }}
      />
    </div>
  )
}

export default function LightboxBeforeAfterSlide({
  before,
  after,
  rect,
  padding = 64,
  defaultPosition = 50,
  leftLabel = 'VORHER',
  rightLabel = 'NACHHER',
}: LightboxBeforeAfterSlideProps) {
  const [position, setPosition] = useState(defaultPosition)

  const intrinsicWidth = before.width ?? after.width
  const intrinsicHeight = before.height ?? after.height
  const { width, height } = computeFitSize(intrinsicWidth, intrinsicHeight, rect, padding)

  const labelCenterLeft = 25
  const labelCenterRight = 75
  const hideRadius = 8
  const showLeft = position > labelCenterLeft + hideRadius
  const showRight = position < labelCenterRight - hideRadius

  return (
    <div className="relative flex h-full w-full items-center justify-center">
      <div className="relative" style={{ width, height }}>
        <ReactCompareSlider
          className="relative h-full w-full"
          onlyHandleDraggable
          defaultPosition={defaultPosition}
          onPositionChange={setPosition}
          itemOne={<SlideImage image={before} width={width} height={height} />}
          itemTwo={<SlideImage image={after} width={width} height={height} />}
          handle={
            <div className="pointer-events-auto absolute inset-0 flex items-center justify-center">
              <div
                className="pointer-events-none absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-white shadow-lg"
                style={{ zIndex: 10 }}
              />
              <div
                aria-label="Schieberegler für Vorher/Nachher"
                className="pointer-events-auto relative flex cursor-ew-resize items-center justify-center"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 9999,
                  background: 'var(--color-card)',
                  border: '2px solid var(--text-white)',
                  boxShadow: '0 1px 2px var(--color-shadow)',
                  zIndex: 40,
                }}
              >
                <ThemeIcon size="size-4" />
              </div>
            </div>
          }
        />

        {/* Bottom-center VORHER / NACHHER labels */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex pb-4">
          <div className="flex w-1/2 justify-center">
            <span
              aria-hidden={!showLeft}
              className={`bg-brand px-3 py-1 font-metropolis text-sm uppercase text-white transition-opacity duration-200 ${
                showLeft ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {leftLabel}
            </span>
          </div>
          <div className="flex w-1/2 justify-center">
            <span
              aria-hidden={!showRight}
              className={`bg-brand px-3 py-1 font-metropolis text-sm uppercase text-white transition-opacity duration-200 ${
                showRight ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {rightLabel}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
